import { createPointRenderer, isLargeCircleLayer, setPointProperties } from './data-point-renderer.js';
import { groundImageStyle } from './data-image-render.js';
import { createRouteMapStyle } from './route-map-style.js';
import { dataIconStyle } from './data-icon-style.js';
import { dataLayerStyleSchema } from '../shared/data-layer-style.js';
import { dataFeatureStyle } from '../shared/data-layer-fields.js';
import { dataFeatureSourceName } from '../shared/data-layer-display.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorImageLayer from 'ol/layer/VectorImage.js';
import VectorSource from 'ol/source/Vector.js';
import { Circle as CircleStyle, Fill, Stroke as OlStroke, Style, Text } from 'ol/style.js';
import type Feature from 'ol/Feature.js';
import type OlMap from 'ol/Map.js';
import type { DataFeature, DataLayer } from '../shared/data-layer.js';
import type { SchemaRecord } from '../shared/schema-input.js';
import { isString } from '../shared/runtime-values.js';

const sourceStyleSchema = dataLayerStyleSchema.partial();

function featureStyle(layer: DataLayer, feature: Feature): Style {
  const styleResult = sourceStyleSchema.safeParse(feature.get('__sourceStyle'));
  const source = styleResult.success ? { ...layer.style, ...styleResult.data } : layer.style;
  const color = source.color;
  const fillColor = source.fillColor ?? color;
  const opacity = source.opacity ?? 1;
  const fillOpacity = source.fillOpacity ?? 0.2;
  const alpha = (hex: string, value: number) => `${hex}${Math.round(Math.max(0, Math.min(1, value)) * 255).toString(16).padStart(2, '0')}`;
  return new Style({
    stroke: new OlStroke({ color: alpha(color, opacity), width: source.width ?? 3, lineDash: feature.get('lineStyle') === 'dashed' ? [8, 6] : undefined }),
    fill: new Fill({ color: alpha(fillColor, fillOpacity) }),
    image: source.icon ? dataIconStyle(source.icon) : new CircleStyle({ radius: source.pointRadius ?? 6, fill: new Fill({ color: alpha(color, opacity) }), stroke: new OlStroke({ color: source.pointOutlineColor ?? '#ffffff', width: 2 }), declutterMode: 'none' }),
    text: layer.display?.showLabels === true && isString(feature.get('__label'))
      ? new Text({ text: feature.get('__label'), overflow: false, padding: [3, 3, 3, 3], offsetY: -14, font: '13px sans-serif', fill: new Fill({ color: '#1e293b' }), stroke: new OlStroke({ color: '#ffffff', width: 3 }) }) : undefined,
  });
}

interface RuntimeGeoJsonFeature { type: 'Feature'; id: string; geometry: DataFeature['geometry']; properties: SchemaRecord }

function geoJsonFeature(feature: DataFeature): RuntimeGeoJsonFeature {
  return {
    type: 'Feature',
    id: feature.id,
    geometry: feature.geometry,
    properties: {
      lineStyle: feature.properties.lineStyle,
      __dataFeatureId: feature.id,
      __sourceStyle: feature.sourceStyle,
    },
  };
}

export function readDataLayerFeatures(map: OlMap, layer: DataLayer, displayed: DataFeature[], selected?: Set<string>): Feature[] {
    const features = new GeoJSON().readFeatures({ type: 'FeatureCollection', features: displayed.map(geoJsonFeature) }, {
      dataProjection: 'EPSG:4326', featureProjection: map.getView().getProjection(),
    });
    features.forEach((feature, index) => {
      feature.set('__dataLayerId', layer.id, true);
      feature.set('__label', dataFeatureSourceName(displayed[index], layer), true);
      feature.set('__sourceStyle', dataFeatureStyle(displayed[index], layer), true);
      setPointProperties(feature, displayed[index], layer, selected?.has(displayed[index].id) ?? false);
    });
    return features;
}

export function createDataLayerSurface(map: OlMap, layer: DataLayer, displayed: DataFeature[], gpu: boolean, selected: () => Set<string> | undefined) {
    const features = readDataLayerFeatures(map, layer, displayed, selected());
    const groundImages = displayed.flatMap(feature => feature.image?.kind === 'ground' ? [{ id: feature.id, image: feature.image }] : []).sort((a, b) => a.image.drawOrder - b.image.drawOrder);
    const imageStyles = new Map(groundImages.map(({ id, image }, index) => {
      const style = groundImageStyle(image, () => map.render());
      style.setZIndex(index - groundImages.length);
      return [id, style];
    }));
    const routeStyles = new Map(displayed.filter(() => layer.sourceFormat === 'gpx').map(feature => [feature.id, createRouteMapStyle(feature, { ...layer.style, ...feature.sourceStyle }, map.getView().getProjection())]));
    const source = new VectorSource({ features });
    const styles = new Map<Feature | string, Style>();
    const selectionStyle = new Style({ stroke: new OlStroke({ color: '#ffffff', width: (layer.style.width ?? 3) + 5 }), image: new CircleStyle({ radius: 10, fill: new Fill({ color: '#ffffff' }), stroke: new OlStroke({ color: '#2563eb', width: 2 }) }) });
    // Large circle layers reuse a buffered image during gestures. Keep every source
    // feature for picking and rebuild the image at the final view resolution.
    const CanvasLayer = isLargeCircleLayer(layer, displayed) ? VectorImageLayer : VectorLayer;
    const vectorLayer = gpu ? createPointRenderer(source, layer.visible) : new CanvasLayer({
      source, visible: layer.visible, imageRatio: 1.5, declutter: 'data-labels',
      style: (feature, resolution) => {
        // SAFETY: This vector source contains OpenLayers Feature values created by GeoJSON.readFeatures.
        const dataFeature = feature as Feature;
        const imageStyle = imageStyles.get(dataFeature.get('__dataFeatureId'));
        if (imageStyle) return imageStyle;
        const styleKey = layer.sourceFormat === 'geojson' && !layer.display?.showLabels
          ? JSON.stringify([dataFeature.get('__sourceStyle'), dataFeature.get('lineStyle') === 'dashed'])
          : dataFeature;
        let style = styles.get(styleKey);
        if (!style) { style = featureStyle(layer, dataFeature); styles.set(styleKey, style); }
        const route = routeStyles.get(dataFeature.get('__dataFeatureId'))?.(resolution) ?? [];
        return selected()?.has(feature.get('__dataFeatureId'))
          ? [selectionStyle, style, ...route]
          : [style, ...route];
      },
    });
    return { source, vectorLayer };
}
